import React, { useState, useEffect, useCallback } from 'react';
import { devError } from '../../lib/devLog';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox, Clock, CheckCircle2, XCircle, MessageCircleQuestion,
  X, Mail, Phone, MapPin, Calendar, Tag, Image as ImageIcon, Trash2,
  Store, BadgePoundSterling, Loader2, ExternalLink, Copy
} from 'lucide-react';
import { withAdminAuth } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:       { label: 'Pending',      icon: Clock,                   color: 'bg-amber-100 text-amber-800' },
  under_review:  { label: 'Under Review', icon: MessageCircleQuestion,   color: 'bg-blue-100 text-blue-800' },
  accepted:      { label: 'Accepted',     icon: CheckCircle2,            color: 'bg-green-100 text-green-800' },
  rejected:      { label: 'Rejected',     icon: XCircle,                 color: 'bg-red-100 text-red-800' },
  needs_info:    { label: 'Needs Info',   icon: MessageCircleQuestion,   color: 'bg-purple-100 text-purple-800' },
  listed:        { label: 'Listed',       icon: Store,                   color: 'bg-indigo-100 text-indigo-800' },
  sold:          { label: 'Sold',         icon: BadgePoundSterling,      color: 'bg-emerald-100 text-emerald-900' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const ConsignmentsManager = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeader = () => ({});  // legacy hook — httpOnly cookie now carries auth

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/consignments`, withAdminAuth({ params }));
      setItems(data.consignments || []);
      setCounts(data.counts_by_status || {});
    } catch (e) {
      devError('Failed to fetch consignments', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/consignments/${id}`,
        { status },
        withAdminAuth(),
      );
      setSelected(data);
      fetchItems();
    } catch (e) {
      alert('Failed to update status: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async (id, admin_notes) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/consignments/${id}`,
        { admin_notes },
        withAdminAuth(),
      );
      setSelected(data);
      fetchItems();
    } catch (e) {
      alert('Failed to save notes: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this consignment permanently? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/consignments/${id}`, withAdminAuth());
      setSelected(null);
      fetchItems();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  const filters = [
    { key: 'all',          label: 'All',          count: totalCount },
    { key: 'pending',      label: 'Pending',      count: counts.pending || 0 },
    { key: 'under_review', label: 'Under Review', count: counts.under_review || 0 },
    { key: 'needs_info',   label: 'Needs Info',   count: counts.needs_info || 0 },
    { key: 'accepted',     label: 'Accepted',     count: counts.accepted || 0 },
    { key: 'listed',       label: 'Listed',       count: counts.listed || 0 },
    { key: 'sold',         label: 'Sold',         count: counts.sold || 0 },
    { key: 'rejected',     label: 'Rejected',     count: counts.rejected || 0 },
  ];

  return (
    <div className="p-6 md:p-8" data-testid="consignments-manager">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Inbox className="w-7 h-7" />
        <h1 className="text-3xl font-light">Consignments Inbox</h1>
      </div>
      <p className="text-sm text-gray-500 mb-8">Review, approve, and reply to consignment submissions.</p>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`consignments-filter-${f.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              filter === f.key
                ? 'bg-black text-white border-black'
                : 'bg-white text-gray-700 border-gray-300 hover:border-black'
            }`}
          >
            {f.label} <span className="ml-1 opacity-70">({f.count})</span>
          </button>
        ))}
      </div>

      {/* Empty / Loading */}
      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="consignments-empty">
          No consignments in this view.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map(c => (
            <button
              key={c.id}
              onClick={() => setSelected(c)}
              data-testid={`consignments-row-${c.id}`}
              className="text-left bg-white border border-gray-200 hover:border-black transition-colors rounded-lg p-4 flex gap-4 items-center"
            >
              {/* Thumb */}
              <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                {c.artwork_images?.[0] ? (
                  <img
                    src={`${BACKEND_URL}${c.artwork_images[0]}`}
                    alt={c.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="font-medium truncate">{c.title} <span className="text-gray-400 font-normal">by {c.artist_name}</span></p>
                  <StatusBadge status={c.status} />
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.submitter?.email}</span>
                  {c.edition_number && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{c.edition_number}</span>}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail Drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setSelected(null)}
            />
            <motion.aside
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed right-0 top-0 bottom-0 w-full md:w-[560px] bg-white z-50 shadow-2xl overflow-y-auto"
              data-testid="consignments-detail-drawer"
            >
              <ConsignmentDetail
                item={selected}
                onClose={() => setSelected(null)}
                onStatusChange={(s) => updateStatus(selected.id, s)}
                onSaveNotes={(n) => saveNotes(selected.id, n)}
                onDelete={() => deleteItem(selected.id)}
                saving={saving}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const ListingAndSaleActions = ({ item }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saleForm, setSaleForm] = useState({
    agreed_price: '',
    currency: 'gbp',
    buyer_email: '',
    buyer_name: '',
    notes: '',
  });
  const [paymentLink, setPaymentLink] = useState(null);

  const authHeader = () => ({});  // legacy hook — httpOnly cookie now carries auth

  const promote = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/consignments/${item.id}/list-on-shop`,
        {},
        withAdminAuth()
      );
      // Reload UI by reloading the page so parent fetches the updated consignment
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const generateLink = async () => {
    setBusy(true); setError(null);
    try {
      const artwork_id = item.artwork_id;
      if (!artwork_id) throw new Error('No artwork is linked to this consignment yet.');
      const price = parseFloat(saleForm.agreed_price);
      if (!price || price <= 0) throw new Error('Enter a valid agreed price.');
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/payments/checkout/create`,
        {
          artwork_id,
          agreed_price: price,
          currency: saleForm.currency,
          buyer_email: saleForm.buyer_email,
          buyer_name: saleForm.buyer_name || undefined,
          origin_url: window.location.origin,
          notes: saleForm.notes || undefined,
        },
        withAdminAuth()
      );
      setPaymentLink(r.data.checkout_url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[consignments] clipboard write failed:', err);
      }
    }
  };

  if (item.status === 'accepted' && !item.artwork_id) {
    return (
      <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4 mb-6" data-testid="consignment-listing-block">
        <p className="text-sm font-medium mb-1 flex items-center gap-2">
          <Store className="w-4 h-4" /> Ready to list
        </p>
        <p className="text-xs text-gray-600 mb-3">
          The first submitted photo will be used as the shop image. Specs (medium,
          dimensions, edition, year) carry over and show on the artwork page.
        </p>
        <button
          onClick={promote}
          disabled={busy}
          data-testid="consignment-list-on-shop-btn"
          className="w-full py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />}
          List on shop
        </button>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    );
  }

  if (item.status === 'listed' || (item.artwork_id && item.status !== 'sold')) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 mb-6" data-testid="consignment-sale-block">
        <p className="text-sm font-medium mb-1 flex items-center gap-2">
          <BadgePoundSterling className="w-4 h-4" /> Generate payment link
        </p>
        <p className="text-xs text-gray-600 mb-3">
          Create a single-use Stripe link for the agreed price. Email it to the
          buyer; we'll mark the work sold automatically once paid.
        </p>
        {item.artwork_id && (
          <a
            href={`/artwork/${item.artwork_id}`}
            target="_blank" rel="noreferrer"
            className="text-xs text-indigo-700 underline mb-3 inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> View live listing
          </a>
        )}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <input
            type="number" step="0.01" min="0"
            placeholder="Price"
            value={saleForm.agreed_price}
            onChange={(e) => setSaleForm({...saleForm, agreed_price: e.target.value})}
            data-testid="sale-agreed-price"
            className="col-span-2 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-black"
          />
          <select
            value={saleForm.currency}
            onChange={(e) => setSaleForm({...saleForm, currency: e.target.value})}
            data-testid="sale-currency"
            className="text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-black"
          >
            <option value="gbp">GBP</option>
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
          </select>
        </div>
        <input
          type="email"
          placeholder="Buyer email"
          value={saleForm.buyer_email}
          onChange={(e) => setSaleForm({...saleForm, buyer_email: e.target.value})}
          data-testid="sale-buyer-email"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mb-2 focus:outline-none focus:border-black"
        />
        <input
          type="text"
          placeholder="Buyer name (optional)"
          value={saleForm.buyer_name}
          onChange={(e) => setSaleForm({...saleForm, buyer_name: e.target.value})}
          data-testid="sale-buyer-name"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mb-2 focus:outline-none focus:border-black"
        />
        <button
          onClick={generateLink}
          disabled={busy || !saleForm.agreed_price || !saleForm.buyer_email}
          data-testid="sale-generate-link-btn"
          className="w-full py-2 bg-emerald-700 text-white text-sm rounded hover:bg-emerald-800 disabled:bg-gray-300 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgePoundSterling className="w-4 h-4" />}
          Generate Stripe link
        </button>
        {paymentLink && (
          <div className="mt-3 bg-white border border-emerald-200 rounded p-2 text-xs break-all" data-testid="sale-payment-link">
            <div className="flex items-center gap-2 mb-2">
              <a href={paymentLink} target="_blank" rel="noreferrer" className="text-emerald-700 underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Open
              </a>
              <button onClick={copyLink} className="text-gray-600 underline flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <code className="text-gray-700">{paymentLink}</code>
          </div>
        )}
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    );
  }

  if (item.status === 'sold') {
    return (
      <div className="border border-emerald-300 bg-emerald-100 rounded-lg p-4 mb-6 text-sm" data-testid="consignment-sold-block">
        <p className="font-medium flex items-center gap-2 text-emerald-900">
          <BadgePoundSterling className="w-4 h-4" /> Sold
        </p>
        <p className="text-xs text-emerald-800 mt-1">
          Payment received. The artwork has been marked as unavailable.
        </p>
      </div>
    );
  }

  return null;
};


const ConsignmentDetail = ({ item, onClose, onStatusChange, onSaveNotes, onDelete, saving }) => {
  const [notes, setNotes] = useState(item.admin_notes || '');
  useEffect(() => { setNotes(item.admin_notes || ''); }, [item.id, item.admin_notes]);

  const statuses = ['pending', 'under_review', 'needs_info', 'accepted', 'rejected'];
  const mailto = `mailto:${item.submitter?.email}?subject=Your%20consignment%20for%20${encodeURIComponent(item.title)}&body=Hi%20${encodeURIComponent(item.submitter?.name || '')}%2C%0A%0AThank%20you%20for%20submitting%20your%20consignment%20to%20Zebra%20One%20Gallery.%20`;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <StatusBadge status={item.status} />
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded" data-testid="consignments-detail-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <h2 className="text-2xl font-light mb-1">{item.title}</h2>
      <p className="text-gray-500 mb-6">by {item.artist_name}</p>

      {/* Photos */}
      {item.labelled_images && Object.keys(item.labelled_images).length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-6" data-testid="consignment-labelled-photos">
          {[['front', 'Front'], ['back', 'Back'], ['edition', 'Edition number'], ['signature', 'Signature']]
            .filter(([k]) => item.labelled_images[k])
            .map(([k, label]) => (
              <a key={k} href={`${BACKEND_URL}${item.labelled_images[k]}`} target="_blank" rel="noreferrer" className="block">
                <img src={`${BACKEND_URL}${item.labelled_images[k]}`} alt={label} className="w-full aspect-square object-cover rounded" />
                <span className="block text-[11px] uppercase tracking-wide text-gray-500 mt-1" data-testid={`consignment-photo-label-${k}`}>{label}</span>
              </a>
            ))}
        </div>
      )}
      {item.artwork_images?.length > 0 && !(item.labelled_images && Object.keys(item.labelled_images).length) && (
        <div className="grid grid-cols-2 gap-2 mb-6">
          {item.artwork_images.map((src, i) => (
            <img key={src} src={`${BACKEND_URL}${src}`} alt={`Artwork ${i+1}`} className="w-full aspect-square object-cover rounded" />
          ))}
        </div>
      )}
      {item.mark_images?.length > 0 && !(item.labelled_images && Object.keys(item.labelled_images).length) && (
        <>
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Signatures & Marks</p>
          <div className="grid grid-cols-3 gap-2 mb-6">
            {item.mark_images.map((src, i) => (
              <img key={src} src={`${BACKEND_URL}${src}`} alt={`Mark ${i+1}`} className="w-full aspect-square object-cover rounded" />
            ))}
          </div>
        </>
      )}

      {/* Artwork fields */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm space-y-2">
        {item.year && <div><span className="text-gray-500">Year:</span> {item.year}</div>}
        {item.medium && <div><span className="text-gray-500">Medium:</span> {item.medium}</div>}
        {item.dimensions && <div><span className="text-gray-500">Dimensions:</span> {item.dimensions}</div>}
        {item.edition_number && <div><span className="text-gray-500">Edition:</span> {item.edition_number}</div>}
        {item.condition && <div><span className="text-gray-500">Condition:</span> {item.condition}</div>}
        {item.purchase_history && <div><span className="text-gray-500">Provenance:</span> {item.purchase_history}</div>}
        {item.current_location && <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" /> {item.current_location}</div>}
      </div>

      {/* Consignee */}
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Consignee</p>
      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm space-y-2" data-testid="consignment-consignee-block">
        <p className="font-medium">{item.submitter?.name}</p>
        <p className="flex items-center gap-2 text-gray-600"><Mail className="w-3.5 h-3.5" /> <a href={mailto} className="underline">{item.submitter?.email}</a></p>
        {item.submitter?.phone && <p className="flex items-center gap-2 text-gray-600"><Phone className="w-3.5 h-3.5" />{item.submitter.phone}</p>}
      </div>

      {/* Status actions */}
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Update Status</p>
      <div className="grid grid-cols-5 gap-1.5 mb-4">
        {statuses.map(s => (
          <button
            key={s}
            onClick={() => onStatusChange(s)}
            disabled={saving || item.status === s}
            data-testid={`consignments-status-${s}`}
            className={`text-xs py-2 rounded border transition-colors ${
              item.status === s
                ? 'bg-black text-white border-black cursor-default'
                : 'bg-white text-gray-700 border-gray-300 hover:border-black disabled:opacity-50'
            }`}
          >
            {STATUS_META[s].label}
          </button>
        ))}
      </div>

      {/* Listing & sale actions */}
      <ListingAndSaleActions item={item} />

      {/* Internal notes */}
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Internal Notes</p>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="consignments-notes-textarea"
        rows={4}
        placeholder="Only visible to admins (e.g. appraisal notes, follow-up actions)"
        className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black mb-3"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSaveNotes(notes)}
          disabled={saving || notes === (item.admin_notes || '')}
          data-testid="consignments-save-notes"
          className="flex-1 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:bg-gray-300"
        >
          {saving ? 'Saving…' : 'Save Notes'}
        </button>
        <a
          href={mailto}
          data-testid="consignments-reply-email"
          className="py-2 px-4 border border-gray-300 text-sm rounded hover:border-black flex items-center gap-2"
        >
          <Mail className="w-4 h-4" /> Reply
        </a>
        <button
          onClick={onDelete}
          data-testid="consignments-delete"
          className="py-2 px-3 border border-red-200 text-red-600 text-sm rounded hover:bg-red-50"
          title="Delete consignment"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ConsignmentsManager;
